'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import ChatTopBar from './ChatTopBar';
import VideoPanel from './VideoPanel';
import TextChatPanel from './TextChatPanel';
import ControlBar from './ControlBar';
import ReportModal from './ReportModal';
import GeoInterestBar from './GeoInterestBar';
import OnboardingModal from './OnboardingModal';

export type ChatState = 'idle' | 'searching' | 'connecting' | 'chatting' | 'disconnected';

export interface ChatMessage {
  id: string;
  sender: 'self' | 'stranger';
  text: string;
  timestamp: Date;
}

export interface PartnerInfo {
  interests: string[];
  location?: string;
  connectedAt: Date;
  anonId?: string;
  name?: string;
  gender?: string;
}

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001';

export default function ChatLayout() {
  const [chatState, setChatState] = useState<ChatState>('idle');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [partnerInfo, setPartnerInfo] = useState<PartnerInfo | null>(null);
  const [geoEnabled, setGeoEnabled] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(0);
  const [anonId, setAnonId] = useState<string | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [gender, setGender] = useState<string>('any');
  const [userName, setUserName] = useState<string>('');
  const [userGender, setUserGender] = useState<string>('');
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);

  useEffect(() => {
    const savedGender = localStorage.getItem('cc_gender');
    if (savedGender) setGender(savedGender);
    const savedName = localStorage.getItem('cc_user_name');
    const savedUserGender = localStorage.getItem('cc_user_gender');
    if (!savedName || !savedUserGender) {
      setShowOnboarding(true);
    } else {
      setUserName(savedName);
      setUserGender(savedUserGender);
    }
  }, []);

  const [isInitiator, setIsInitiator] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('cc_anon_id');
    if (stored) { setAnonId(stored); return; }
    fetch(`${BACKEND_URL}/api/session/start`, { method: 'POST' })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          localStorage.setItem('cc_anon_id', data.anonId);
          setAnonId(data.anonId);
        }
      })
      .catch((err) => console.error('Session start failed:', err));
  }, []);

  useEffect(() => {
    if (!anonId) return;
    const socket = io(BACKEND_URL, {
      auth: { anonId },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('match:found', ({ roomId: rid, geoMatched, initiatorId, commonInterests, partnerAnonId, partnerName, partnerGender, partnerLocation }) => {
      setIsInitiator(initiatorId === socket.id);
      setRoomId(rid);
      setPartnerInfo({
        interests: commonInterests || [],
        location: partnerLocation || (geoMatched ? 'Nearby' : undefined),
        connectedAt: new Date(),
        anonId: partnerAnonId || undefined,
        name: partnerName || 'Anonymous',
        gender: partnerGender || 'any',
      });
      setChatState('connecting');
      setTimeout(() => {
        setChatState('chatting');
        setSessionTimer(0);
      }, 4000);
    });

    socket.on('queue:waiting', () => setChatState('searching'));

    socket.on('chat:message', ({ text, timestamp }) => {
      setMessages((prev) => [...prev, {
        id: `msg-${Date.now()}`,
        sender: 'stranger',
        text,
        timestamp: new Date(timestamp),
      }]);
    });

    socket.on('user:disconnect', () => {
      setIsInitiator(false);
      setRoomId(null);
      setChatState('disconnected');
      setPartnerInfo(null);
      if (timerRef.current) clearInterval(timerRef.current);
    });

    socket.on('connect_error', (err) => console.error('Socket error:', err.message));

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [anonId]);

  useEffect(() => {
    if (chatState === 'chatting') {
      timerRef.current = setInterval(() => setSessionTimer((t) => t + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [chatState]);

  const handleStart = useCallback(() => {
    if (!socketRef.current) return;
    setChatState('searching');
    setMessages([]);
    setPartnerInfo(null);
    setSessionTimer(0);
    setShowMobileSettings(false);

    const payload: { geoEnabled: boolean; interests: string[]; lat?: number; lng?: number; gender?: string; matchWith?: string; name?: string } = {
      geoEnabled, interests,
      gender: userGender || 'any',
      matchWith: gender,
      name: userName || 'Anonymous',
    };

    if (geoEnabled && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { payload.lat = pos.coords.latitude; payload.lng = pos.coords.longitude; socketRef.current?.emit('join:queue', payload); },
        () => { payload.geoEnabled = false; socketRef.current?.emit('join:queue', payload); }
      );
    } else {
      socketRef.current.emit('join:queue', payload);
    }
  }, [geoEnabled, interests, userName, userGender, gender]);

  const handleSkip = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.emit('user:skip');
    setChatState('searching');
    setMessages([]);
    setPartnerInfo(null);
    setSessionTimer(0);
    setIsInitiator(false);
    setRoomId(null);
    setTimeout(() => {
      socketRef.current?.emit('join:queue', {
        geoEnabled, interests,
        gender: userGender || 'any',
        matchWith: gender,
        name: userName || 'Anonymous',
      });
    }, 800);
  }, [geoEnabled, interests, userName, userGender, gender]);

  const handleCancel = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.emit('queue:leave');
    setChatState('idle');
    setMessages([]);
    setPartnerInfo(null);
    setSessionTimer(0);
    setIsInitiator(false);
    setRoomId(null);
  }, []);

  const handleSendMessage = useCallback((text: string) => {
    if (!socketRef.current || chatState !== 'chatting') return;
    setMessages((prev) => [...prev, {
      id: `msg-${Date.now()}`,
      sender: 'self',
      text,
      timestamp: new Date(),
    }]);
    socketRef.current.emit('chat:message', { text });
  }, [chatState]);

  const handleReport = useCallback((reason: string, details: string) => {
    setIsReportOpen(false);
    if (!anonId || !roomId) return;
    fetch(`${BACKEND_URL}/api/match/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reporterAnonId: anonId,
        reportedAnonId: partnerInfo?.anonId || 'unknown',
        reason, details, sessionId: roomId,
      }),
    }).catch(console.error);
  }, [anonId, roomId, partnerInfo]);

  const handleOnboardingComplete = (name: string, uGender: string) => {
    setUserName(name);
    setUserGender(uGender);
    localStorage.setItem('cc_user_name', name);
    localStorage.setItem('cc_user_gender', uGender);
    setShowOnboarding(false);
  };

  const isSearching = chatState === 'searching' || chatState === 'connecting';
  const isActive = chatState === 'chatting';

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}

      {/* ── DESKTOP layout ── */}
      <div className="hidden md:flex md:flex-col h-full">
        <ChatTopBar chatState={chatState} partnerInfo={partnerInfo} sessionTimer={sessionTimer} />
        <GeoInterestBar
          geoEnabled={geoEnabled} onGeoToggle={setGeoEnabled}
          interests={interests} onInterestsChange={setInterests}
          chatState={chatState} gender={gender}
          onGenderChange={(g) => { setGender(g); localStorage.setItem('cc_gender', g); }}
        />
        <div className="flex flex-row flex-1 overflow-hidden relative">
          <div className="flex-1 min-w-0 relative">
            <VideoPanel chatState={chatState} partnerInfo={partnerInfo} isMuted={isMuted} isCameraOff={isCameraOff} socket={socketRef.current} isInitiator={isInitiator} />
          </div>
          <div className="w-80 xl:w-96 flex-shrink-0 border-l border-border flex flex-col bg-card">
            <TextChatPanel messages={messages} onSendMessage={handleSendMessage} chatState={chatState} />
          </div>
        </div>
        <ControlBar chatState={chatState} isMuted={isMuted} isCameraOff={isCameraOff} onSkip={handleSkip} onCancel={handleCancel} onStart={handleStart} onMuteToggle={() => setIsMuted(v => !v)} onCameraToggle={() => setIsCameraOff(v => !v)} onReport={() => setIsReportOpen(true)} />
      </div>

      {/* ── MOBILE layout ── */}
      <div className="md:hidden flex flex-col h-full relative bg-black">

        {/* Fullscreen video */}
        <div className="absolute inset-0 z-0">
          <VideoPanel chatState={chatState} partnerInfo={partnerInfo} isMuted={isMuted} isCameraOff={isCameraOff} socket={socketRef.current} isInitiator={isInitiator} />
        </div>

        {/* Top bar overlay */}
        <div className="relative z-10 flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white text-xs font-semibold">ChatConnect</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Status */}
            <div className={`px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-md ${
              isActive ? 'bg-green-500/30 text-green-300' :
              isSearching ? 'bg-yellow-500/30 text-yellow-300' :
              'bg-black/40 text-white/60'
            }`}>
              {isActive ? '● Connected' : isSearching ? '● Searching...' : chatState === 'disconnected' ? '● Disconnected' : '● Idle'}
            </div>

            {/* Settings toggle */}
            <button
              onClick={() => setShowMobileSettings(v => !v)}
              className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center"
            >
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Settings panel (slide down) */}
        {showMobileSettings && (
          <div className="relative z-20 mx-3 mb-2 bg-black/80 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <GeoInterestBar
              geoEnabled={geoEnabled} onGeoToggle={setGeoEnabled}
              interests={interests} onInterestsChange={setInterests}
              chatState={chatState} gender={gender}
              onGenderChange={(g) => { setGender(g); localStorage.setItem('cc_gender', g); }}
            />
          </div>
        )}

        {/* Middle spacer */}
        <div className="flex-1 relative z-10">
          {/* Partner name overlay when chatting */}
          {isActive && partnerInfo?.name && (
            <div className="absolute bottom-2 left-3 flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
              <span className="text-white text-xs font-medium">{partnerInfo.name}</span>
              {partnerInfo.gender && partnerInfo.gender !== 'any' && (
                <span className={`text-xs ${partnerInfo.gender === 'male' ? 'text-blue-400' : 'text-pink-400'}`}>
                  {partnerInfo.gender === 'male' ? '♂' : '♀'}
                </span>
              )}
              {partnerInfo.location && (
                <span className="text-white/50 text-xs">📍 {partnerInfo.location}</span>
              )}
            </div>
          )}
        </div>

        {/* Chat messages overlay (when chatting) */}
        {isActive && showMobileChat && (
          <div className="relative z-20 mx-3 mb-2 h-56 bg-black/70 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden flex flex-col">
            <TextChatPanel messages={messages} onSendMessage={handleSendMessage} chatState={chatState} />
          </div>
        )}

        {/* Bottom controls */}
        <div className="relative z-10 px-4 pb-6 pt-2">

          {/* Chat toggle + input row (when chatting) */}
          {isActive && !showMobileChat && (
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => setShowMobileChat(true)}
                className="flex-1 flex items-center gap-2 bg-black/50 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-3"
              >
                <svg className="w-4 h-4 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-white/50 text-sm">
                  {messages.length > 0 ? `${messages.length} messages` : 'Type a message...'}
                </span>
              </button>
              {messages.length > 0 && (
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{messages.length}</span>
                </div>
              )}
            </div>
          )}

          {/* Close chat button */}
          {isActive && showMobileChat && (
            <button
              onClick={() => setShowMobileChat(false)}
              className="w-full flex items-center justify-center gap-2 mb-3 bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl py-2"
            >
              <svg className="w-4 h-4 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span className="text-white/50 text-xs">Hide chat</span>
            </button>
          )}

          {/* Main action buttons */}
          <div className="flex items-center justify-center gap-3">

            {/* Mute */}
            <button
              onClick={() => setIsMuted(v => !v)}
              disabled={!isActive}
              className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                !isActive ? 'bg-white/5 opacity-30' :
                isMuted ? 'bg-red-500/30 border border-red-500/50' : 'bg-white/10 backdrop-blur-md border border-white/20'
              }`}
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMuted
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                }
              </svg>
              <span className="text-white/60 text-[10px]">{isMuted ? 'Unmute' : 'Mute'}</span>
            </button>

            {/* Camera */}
            <button
              onClick={() => setIsCameraOff(v => !v)}
              disabled={!isActive}
              className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                !isActive ? 'bg-white/5 opacity-30' :
                isCameraOff ? 'bg-red-500/30 border border-red-500/50' : 'bg-white/10 backdrop-blur-md border border-white/20'
              }`}
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.876V15.124a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
              </svg>
              <span className="text-white/60 text-[10px]">{isCameraOff ? 'Cam On' : 'Cam Off'}</span>
            </button>

            {/* Main CTA */}
            {(chatState === 'idle' || chatState === 'disconnected') && (
              <button
                onClick={handleStart}
                className="w-24 h-14 rounded-2xl bg-primary flex flex-col items-center justify-center gap-1 active:scale-95 shadow-lg"
              >
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                </svg>
                <span className="text-white text-xs font-semibold">{chatState === 'disconnected' ? 'Next' : 'Start'}</span>
              </button>
            )}

            {isSearching && (
              <button
                onClick={handleCancel}
                className="w-24 h-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex flex-col items-center justify-center gap-1 active:scale-95"
              >
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-white text-xs font-semibold">Cancel</span>
              </button>
            )}

            {isActive && (
              <button
                onClick={handleSkip}
                className="w-24 h-14 rounded-2xl bg-primary flex flex-col items-center justify-center gap-1 active:scale-95 shadow-lg"
              >
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <span className="text-white text-xs font-semibold">Next</span>
              </button>
            )}

            {/* Report */}
            <button
              onClick={() => setIsReportOpen(true)}
              disabled={!isActive}
              className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                isActive ? 'bg-red-500/20 border border-red-500/30' : 'bg-white/5 opacity-30'
              }`}
            >
              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
              <span className="text-red-400/70 text-[10px]">Report</span>
            </button>
          </div>
        </div>
      </div>

      <ReportModal isOpen={isReportOpen} onClose={() => setIsReportOpen(false)} onSubmit={handleReport} />
    </div>
  );
}
